/**
 * Pure client-side booking-form validation, mirroring the shared schema.
 *
 * Client-side validation MUST agree with the authoritative server-side checks
 * so a Visitor is never told a submission is valid only to have the API reject
 * it. To guarantee that, this module does not re-implement any rules: it calls
 * the very same `validateBookingSubmission` the Lambda uses (Req 8.2, 8.3) and
 * simply reshapes its `BookingValidationError[]` into a per-field message map
 * the form can render inline. `FIELD_LIMITS` / `EMAIL_PATTERN` are reached
 * transitively through that shared validator.
 *
 * Keeping this reshaping pure (no React, no DOM) makes the field-error mapping
 * directly unit-testable.
 */

import {
  validateBookingSubmission,
  type BookingField,
  type BookingSubmission,
} from "@photography-booking/shared";

/** A per-field map of human-readable validation messages. */
export type FieldErrors = Partial<Record<BookingField, string>>;

/** A selectable session-length option for the booking form's duration picker. */
export interface DurationOption {
  /** Duration in whole minutes (the value sent as `durationMinutes`). */
  minutes: number;
  /** Human-readable label, e.g. "1 hour 30 minutes". */
  label: string;
}

/** Default session length (minutes) pre-selected on the booking form. */
export const DEFAULT_DURATION_MINUTES = 60;

/** Renders a minute count as a friendly "H hour(s) M minute(s)" label. */
function formatDurationLabel(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  const parts: string[] = [];
  if (hours > 0) {
    parts.push(`${hours} hour${hours === 1 ? "" : "s"}`);
  }
  if (mins > 0) {
    parts.push(`${mins} minute${mins === 1 ? "" : "s"}`);
  }
  return parts.join(" ");
}

/**
 * The session-length choices offered by the booking form's duration selector.
 *
 * Every value sits within the shared 15..720 minute range enforced by
 * `validateBookingSubmission`, so any selection is accepted server-side.
 */
export const DURATION_OPTIONS: readonly DurationOption[] = [
  // Sessions start at 1 hour: a 30-minute shoot is not offered.
  60, 90, 120, 180, 240, 300, 360,
].map((minutes) => ({ minutes, label: formatDurationLabel(minutes) }));

/**
 * Validates a submission with the shared validator and returns the first error
 * message for each offending field (an empty object means the submission is
 * valid). Reporting one message per field keeps the inline form display clean
 * while still naming every problematic field at once.
 */
export function collectFieldErrors(submission: BookingSubmission): FieldErrors {
  const result = validateBookingSubmission(submission);
  if (result.valid) {
    return {};
  }

  const errors: FieldErrors = {};
  for (const error of result.errors) {
    // Keep the first message seen for a field; the shared validator already
    // reports at most one error per (field, rule) and orders them sensibly.
    if (errors[error.field] === undefined) {
      errors[error.field] = error.message;
    }
  }
  return errors;
}

/** True iff the submission passes every shared validation rule. */
export function isSubmissionValid(submission: BookingSubmission): boolean {
  return validateBookingSubmission(submission).valid;
}
